import {
  CalendarOutlined,
  CheckOutlined,
  ClockCircleOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import { Avatar } from 'antd';
import {
  APPOINTMENT_STATUS,
  appointmentStatusObject,
} from 'features/appointment/services/constants';
import { IAptGroupByTimeAppointmentData } from 'features/user/services/types/apt';
import moment from 'moment';
import { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { formatPhone, maskPhone } from 'utils/formatPhone';
import { momentTimezone } from 'utils/time';
import RequestAdminPWCallback, { RequestAdminPWCallbackRef } from 'widgets/RequestAdminLocalPW/Callback';

type Props = {
  data: IAptGroupByTimeAppointmentData | null;
};

const formatClock = (value: string) => {
  const m = momentTimezone(value);
  return m.isValid() ? m.format('hh:mm A') : value;
};

const initialsFromName = (name: string) => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0] ?? ''}${parts[parts.length - 1][0] ?? ''}`.toUpperCase();
  }
  return name.slice(0, 2).toUpperCase() || '?';
};

const getStatusLabel = (status: number) => {
  const map = appointmentStatusObject as Partial<Record<number, string>>;
  if (map[status]) return map[status] as string;
  if (status === APPOINTMENT_STATUS.PAID) return 'Paid';
  if (status === APPOINTMENT_STATUS.ON_HOLD) return 'On hold';
  return 'Unknown';
};

const getStatusTone = (
  status: number,
): 'success' | 'info' | 'neutral' | 'danger' => {
  if (status === APPOINTMENT_STATUS.CONFIRM || status === APPOINTMENT_STATUS.CHECK_IN) {
    return 'success';
  }
  if (status === APPOINTMENT_STATUS.NEW || status === APPOINTMENT_STATUS.ON_HOLD) {
    return 'neutral';
  }
  if (status === APPOINTMENT_STATUS.CANCEL || status === APPOINTMENT_STATUS.NO_SHOW) {
    return 'danger';
  }
  if (status === APPOINTMENT_STATUS.PAID) return 'info';
  return 'neutral';
};

const ApptDetailUI = ({ data }: Props) => {
  const [phoneVisible, setPhoneVisible] = useState(false);
  const requestAdminPWCallbackRef = useRef<RequestAdminPWCallbackRef>(null);
  const onChangePhoneVisible = () => {
    const nextValue = !phoneVisible;
    if (nextValue) {
      requestAdminPWCallbackRef.current?.open(() => {
        setPhoneVisible(nextValue);
      });
      return;
    }
    setPhoneVisible((v) => !v);
  };

  useEffect(() => {
    setPhoneVisible(false);
  }, [data?.appointmentId]);

  const dateLabel = useMemo(() => {
    if (!data) return '';
    const m = momentTimezone(data.startTime);
    return m.isValid() ? m.format('dddd, MMM D') : '';
  }, [data]);

  const timeRange = useMemo(() => {
    if (!data) return '';
    return `${formatClock(data.startTime)} - ${formatClock(data.endTime)}`;
  }, [data]);

  const note = useMemo(() => {
    if (!data) return null;
    return (data as IAptGroupByTimeAppointmentData & { note?: string | null }).note;
  }, [data]);

  if (!data) return null;

  const statusLabel = getStatusLabel(data.status);
  const statusTone = getStatusTone(data.status);
  const phoneDisplay = phoneVisible
    ? formatPhone(data.customerPhone) || data.customerPhone
    : maskPhone(data.customerPhone);

  return (
    <Outer>
      <Card>
        <HeaderRow>
          <CustomerBlock>
            <CustomerAvatar aria-hidden>{initialsFromName(data.customerName)}</CustomerAvatar>
            <CustomerText>
              <CustomerName title={data.customerName}>{data.customerName}</CustomerName>
              <PhoneRow>
                <PhoneText>{phoneDisplay || '—'}</PhoneText>
                <PhoneToggle
                  type='button'
                  aria-label={phoneVisible ? 'Hide phone number' : 'Show phone number'}
                  onClick={onChangePhoneVisible}
                >
                  {phoneVisible ? (
                    <EyeInvisibleOutlined style={{ fontSize: 16, color: '#64748b' }} />
                  ) : (
                    <EyeOutlined style={{ fontSize: 16, color: '#64748b' }} />
                  )}
                </PhoneToggle>
              </PhoneRow>
            </CustomerText>
          </CustomerBlock>
          <StatusBadge $tone={statusTone}>
            {statusTone === 'success' ? (
              <CheckOutlined style={{ fontSize: 12, color: '#fff' }} />
            ) : null}
            <StatusBadgeText>{statusLabel}</StatusBadgeText>
          </StatusBadge>
        </HeaderRow>

        <DateTimeRow>
          <DateTimeCell>
            <IconTile>
              <CalendarOutlined style={{ fontSize: 22, color: '#2d6772' }} />
            </IconTile>
            <DateTimeLabels>
              <MetaLabel>DATE</MetaLabel>
              <MetaValue>{dateLabel || '—'}</MetaValue>
            </DateTimeLabels>
          </DateTimeCell>
          <DateTimeCell>
            <IconTile>
              <ClockCircleOutlined style={{ fontSize: 22, color: '#2d6772' }} />
            </IconTile>
            <DateTimeLabels>
              <MetaLabel>TIME</MetaLabel>
              <MetaValue>{timeRange || '—'}</MetaValue>
            </DateTimeLabels>
          </DateTimeCell>
        </DateTimeRow>

        <ServicesBlock>
          {data.items.map((item, idx) => (
            <ServiceRow
              key={`${item.serviceId}-${item.staffId}-${item.indexWorking}`}
              $isFirst={idx === 0}
              $isLast={idx === data.items.length - 1}
            >
              <ServiceText>
                <ServiceName>{item.serviceName || '—'}</ServiceName>
                <ServiceDuration>{item.duration ? `${item.duration} mins` : '—'}</ServiceDuration>
              </ServiceText>
              <StaffBlock>
                <StaffAvatarWrap>
                  <StaffAvatar
                    src={item.staffAvatar || undefined}
                    size={40}
                  >
                    {item.staffName?.[0] ?? '?'}
                  </StaffAvatar>
                  {item.requestStaff ? <RequestBadge>R</RequestBadge> : null}
                </StaffAvatarWrap>
                <StaffName>{item.staffName || '—'}</StaffName>
              </StaffBlock>
            </ServiceRow>
          ))}
        </ServicesBlock>

        {note ? (
          <NoteBox>
            <NoteLabel>Note</NoteLabel>
            <NoteBody>&ldquo;{note}&rdquo;</NoteBody>
          </NoteBox>
        ) : null}
      </Card>
      <RequestAdminPWCallback
        v2
        ref={requestAdminPWCallbackRef}
      />
    </Outer>
  );
};

export default ApptDetailUI;

const Outer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  padding: 16px;
  width: 100%;
  box-sizing: border-box;
  background: #F6F7FC;
`;

const Card = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  padding: 24px;
  background: #ffffff;
  border-radius: 24px;
  box-sizing: border-box;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  width: 100%;
  padding-bottom: 24px;
  border-bottom: 1px solid #f1f5f9;
`;

const CustomerBlock = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  min-width: 0;
`;

const CustomerAvatar = styled.div`
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  border-radius: 100px;
  background: #2563eb;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'Poppins', sans-serif;
  font-weight: 600;
  font-size: 18px;
  line-height: 30px;
  color: #ffffff;
  text-transform: capitalize;
`;

const CustomerText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: flex-start;
  min-width: 0;
`;

const CustomerName = styled.p`
  margin: 0;
  font-family: 'Poppins', sans-serif;
  font-weight: 600;
  font-size: 20px;
  line-height: normal;
  color: #1d2129;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
`;

const PhoneRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
`;

const PhoneText = styled.span`
  font-family: 'Poppins', sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: normal;
  color: #64748b;
  white-space: nowrap;
`;

const PhoneToggle = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin: 0;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  line-height: 0;
`;

const StatusBadge = styled.div<{ $tone: 'success' | 'info' | 'neutral' | 'danger' }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  flex-shrink: 0;
  padding: 4px 8px;
  border-radius: 100px;
  background: ${(p) =>
    p.$tone === 'success'
      ? '#0da472'
      : p.$tone === 'info'
        ? '#2563eb'
        : p.$tone === 'danger'
          ? '#f5767f'
          : '#94a3b8'};
`;

const StatusBadgeText = styled.span`
  font-family: 'Poppins', sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: normal;
  color: #ffffff;
  white-space: nowrap;
`;

const DateTimeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
  width: 100%;
  padding-bottom: 16px;
  border-bottom: 1px solid #e9edf2;
  border-radius: 16px 16px 0 0;
`;

const DateTimeCell = styled.div`
  display: flex;
  flex: 1;
  align-items: center;
  gap: 12px;
  min-width: 0;
`;

const IconTile = styled.div`
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  border-radius: 8px;
  background: #e4f4f6;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const DateTimeLabels = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  align-items: flex-start;
  min-width: 0;
`;

const MetaLabel = styled.span`
  font-family: 'Poppins', sans-serif;
  font-weight: 500;
  font-size: 16px;
  line-height: normal;
  color: #2d6772;
`;

const MetaValue = styled.span`
  font-family: 'Poppins', sans-serif;
  font-weight: 500;
  font-size: 20px;
  line-height: normal;
  color: #1d2129;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
`;

const ServicesBlock = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const ServiceRow = styled.div<{ $isFirst: boolean; $isLast: boolean }>`
  display: flex;
  align-items: center;
  gap: 24px;
  width: 100%;
  padding-top: ${(p) => (p.$isFirst ? '16px' : '8px')};
  padding-bottom: ${(p) => (p.$isLast ? '0' : '8px')};
  border-bottom: ${(p) => (p.$isLast ? 'none' : '1px solid #e9edf2')};
`;

const ServiceText = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 2px;
  align-items: flex-start;
  min-width: 0;
`;

const ServiceName = styled.p`
  margin: 0;
  width: 100%;
  font-family: 'Poppins', sans-serif;
  font-weight: 500;
  font-size: 20px;
  line-height: normal;
  color: #1d2129;
`;

const ServiceDuration = styled.p`
  margin: 0;
  width: 100%;
  font-family: 'Poppins', sans-serif;
  font-weight: 500;
  font-size: 16px;
  line-height: normal;
  color: #64748b;
`;

const StaffBlock = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
`;

const StaffAvatarWrap = styled.span`
  position: relative;
  display: inline-flex;
  align-items: center;
`;

const StaffAvatar = styled(Avatar)`
  flex-shrink: 0;
  border: 1px solid #ffffff;
`;

const RequestBadge = styled.span`
  position: absolute;
  left: 50%;
  bottom: -7px;
  transform: translateX(-50%);
  min-width: 16px;
  height: 16px;
  padding: 0 6px;
  border-radius: 100px;
  background: #2563eb;
  border: 1px solid #ffffff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-family: 'Poppins', sans-serif;
  font-weight: 600;
  font-size: 12px;
  line-height: 14px;
  color: #ffffff;
  box-sizing: border-box;
`;

const StaffName = styled.span`
  font-family: 'Poppins', sans-serif;
  font-weight: 500;
  font-size: 16px;
  line-height: normal;
  color: #1d2129;
  white-space: nowrap;
`;

const NoteBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  width: 100%;
  padding: 8px;
  border-radius: 8px;
  background: #f6f7fc;
  line-height: normal;
`;

const NoteLabel = styled.p`
  margin: 0;
  width: 100%;
  font-family: 'Poppins', sans-serif;
  font-weight: 500;
  font-size: 14px;
  color: #64748b;
`;

const NoteBody = styled.p`
  margin: 0;
  width: 100%;
  font-family: 'Poppins', sans-serif;
  font-weight: 500;
  font-style: italic;
  font-size: 16px;
  color: #1d2129;
`;
